import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type PropsWithChildren,
    type ReactNode
} from 'react';
import Api from '../util/Api';
import {
    persistSessionKey,
    clearPersistedSessionKey,
    purgeLegacyCredentialStorage
} from '../services/auth';
import { buildUserFromAuthResponse } from '../util/auth-user';
import type { SessionRefusalReason } from '../services/session-refusal';
import type { AuthResponse, User } from '../types/api';

export type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

export interface SessionState {
    user: User | undefined;
    accessToken: string | undefined;
    refreshToken: string | undefined;
    validTo: Date | undefined;
    sessionKey: string | undefined;
    status: SessionStatus;
}

interface SessionContextValue extends SessionState {
    /** Run authorize (e.g. with stored session key). Resolves to true if authenticated. */
    authorize: (sessionKey?: string) => Promise<boolean>;
    /**
     * Log in with email and password. `rememberMe` (default true) controls whether the
     * session survives a browser restart — false keeps it in memory for this tab only, per
     * the login screen's "Keep me signed in" checkbox. Resolves to true if successful.
     */
    login: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
    /** Clear session and mark unauthenticated. */
    logout: () => void;
    /** Whether the user is currently authenticated. */
    isAuthenticated: boolean;
    /** Current user's display name (userName). */
    userName: string | undefined;
    /** Current user's permissions (roles). */
    permissions: string[];
    /** Current user type. */
    userType: User['userType'] | undefined;
    /** Profile photo media object for avatar/display. */
    profilePhoto: User['userMediaObj'] | undefined;
    /** Super-user's display name (when impersonating). */
    superUserName: string | undefined;
    /** Super-user's profile photo (when impersonating). */
    superUserProfilePhoto: User['superUserMediaObj'] | undefined;
    /**
     * ROBO-345. Whether the nav may offer "Back to &lt;super user&gt;".
     *
     * NOT the same question as "is there a super user". A spawned impersonation session records one
     * it must not return to — see {@link User.superNoRevert} — so the presence of a super user is
     * necessary but not sufficient.
     */
    canRevertToSuperUser: boolean;
    /** Whether the current user is an admin. */
    isAdmin: boolean;
    /**
     * The brand the current user belongs to, as a number.
     *
     * ★ `User.brandId` IS A STRING ON THE WIRE and this is not. Every caller wants to compare it with
     * a brand id that arrived as a number (`site.brandId`, an order row's `brandId`), and `'12' === 12`
     * is false — a comparison that fails this way looks like a permission decision rather than a type
     * bug, which is exactly how it hides. Converted once here so no caller has to remember.
     *
     * `undefined` while unauthenticated, and also if the id is ever unparseable. Treat it as unknown
     * and fail closed; do not fall back to 0, which is a brand id shape and would compare equal to
     * something.
     */
    brandId: number | undefined;
    /**
     * ROBO-478. Why the server last refused this browser a session, if it did — today only
     * `'brand-suspended'` (a suspended business's non-Owner user). Set when login, the boot
     * authorize, or a mid-session refresh is refused; the session is already ended by then and
     * `status` is `'unauthenticated'`. Cleared when a new login or authorize starts, on success, and
     * on logout.
     *
     * Held HERE rather than in the login form because the form does not survive the attempt: the hub
     * page and the studio layout both swap it for a spinner while `status` is `'loading'`, so any
     * state the form kept for itself is gone when it remounts.
     *
     * Optional so existing `useSession` mocks keep compiling; absent means no refusal.
     */
    refusal?: SessionRefusalReason;
}

/** Parses `User.brandId`, which is a string, into the number every consumer compares against. */
function parseBrandId(value: string | undefined): number | undefined {
    if (value === undefined) return undefined;

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * ROBO-345. Whether the nav may offer "Back to &lt;super user&gt;".
 *
 * Two conditions, and the second is the one that is easy to forget: there must BE a super user, and
 * the session must not be one that cannot go back to them. A spawned impersonation session is
 * exactly that — it records a super user for display (the avatar still shows who is behind it) but
 * opens in its own tab on its own host, while the impersonator's real session stays live in the tab
 * they came from. Reverting there would restore them into a tab they never left.
 *
 * Exported and pure so the rule can be tested without standing up the provider.
 */
export function canRevertToSuperUser(user: User | undefined): boolean {
    return Boolean(user?.superUserId) && !user?.superNoRevert;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: PropsWithChildren<{ children?: ReactNode }>) {
    const [state, setState] = useState<SessionState>({
        user: undefined,
        accessToken: undefined,
        refreshToken: undefined,
        validTo: undefined,
        sessionKey: undefined,
        status: 'idle'
    });
    const [refusal, setRefusal] = useState<SessionRefusalReason | undefined>(undefined);

    const setSessionFromAuth = useCallback((data: AuthResponse | undefined, persist = true) => {
        if (!data) {
            setState({
                user: undefined,
                accessToken: undefined,
                refreshToken: undefined,
                validTo: undefined,
                sessionKey: undefined,
                status: 'unauthenticated'
            });
            return;
        }
        setRefusal(undefined);
        const user = buildUserFromAuthResponse(data);
        // Persist so a full document load can restore the session via authorize() on boot.
        // Covers every auth path (login, authorize, refresh), not just the login form. Skipped
        // only when the login screen's "Keep me signed in" checkbox was unchecked, so the
        // session lives in memory for this tab and does not survive a browser restart.
        if (persist) {
            persistSessionKey(data.sessionKey);
        }
        setState({
            user,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            validTo: new Date(data.validTo),
            sessionKey: data.sessionKey,
            status: 'authenticated'
        });
    }, []);

    // ROBO-275: clear plaintext credentials that older builds wrote to localStorage. Runs at the
    // provider, not on the login page, so users who are already signed in are cleaned up too.
    useEffect(() => {
        purgeLegacyCredentialStorage();
    }, []);

    useEffect(() => {
        const prev = Api.onSessionUpdate;
        Api.onSessionUpdate = (data: AuthResponse, persist?: boolean) => {
            setSessionFromAuth(data, persist);
        };
        return () => {
            Api.onSessionUpdate = prev;
        };
    }, [setSessionFromAuth]);

    /*
     * ROBO-478. Api has already ended the session (tokens, timers, persisted key) before calling
     * this, so all that is left is React state: drop to unauthenticated and record why. The studio
     * layout and the hub login page both render the login form for that status, and the form reads
     * `refusal` to show the Account Inactive notice. No navigation and no retry from here.
     */
    useEffect(() => {
        const prev = Api.onSessionRefused;
        Api.onSessionRefused = (reason: SessionRefusalReason) => {
            setSessionFromAuth(undefined);
            setRefusal(reason);
        };
        return () => {
            Api.onSessionRefused = prev;
        };
    }, [setSessionFromAuth]);

    const authorize = useCallback(async (sessionKey?: string): Promise<boolean> => {
        setRefusal(undefined);
        setState(s => ({ ...s, status: 'loading' }));
        try {
            const token = await Api.authorize(sessionKey);
            if (token) {
                return true;
            }
            setState(s => ({ ...s, status: 'unauthenticated' }));
            return false;
        } catch {
            setState(s => ({ ...s, status: 'unauthenticated' }));
            return false;
        }
    }, []);

    const login = useCallback(
        async (email: string, password: string, rememberMe: boolean = true): Promise<boolean> => {
            // A new attempt must not inherit the last one's refusal: a wrong password after a
            // BRAND_SUSPENDED refusal is an ordinary 401 and must read as one.
            setRefusal(undefined);
            /*
             * ROBO-483. Deliberately NOT `status: 'loading'` here — do not "restore" it.
             *
             * The page-level spinner that `loading` turns on (hub-login-page.tsx, and the studio
             * layout-component.tsx) exists to cover the boot / Google-return `authorize`, where
             * there is no form worth keeping on screen. A form login is the opposite case: it
             * already shows its own progress in the Sign In button via the form's `authorizing`
             * flag, so the page-level spinner adds nothing — and it costs the form everything it
             * holds. Swapping it for the spinner unmounts it, so a failed attempt came back as a
             * fresh form with no error message and an empty email field, which is the bug.
             *
             * Fixed here rather than at the two page gates because each gate would need the same
             * exemption, and the next shell to render `<Login>` would rediscover it. Lifting the
             * form's `failed` flag into this context (the way `refusal` is) would bring the message
             * back but not the typed email, which is local state too.
             */
            try {
                const token = await Api.login(email, password, rememberMe);
                if (token) {
                    return true;
                }
                setState(s => ({ ...s, status: 'unauthenticated' }));
                return false;
            } catch {
                setState(s => ({ ...s, status: 'unauthenticated' }));
                return false;
            }
        },
        []
    );

    const logout = useCallback(() => {
        clearPersistedSessionKey();
        Api.accessToken = undefined;
        Api.refreshToken = undefined;
        Api.user = undefined;
        Api.validTo = undefined;
        if (Api.refreshTimer) {
            clearTimeout(Api.refreshTimer);
            Api.refreshTimer = undefined;
        }
        setRefusal(undefined);
        setState({
            user: undefined,
            accessToken: undefined,
            refreshToken: undefined,
            validTo: undefined,
            sessionKey: undefined,
            status: 'unauthenticated'
        });
    }, []);

    const value = useMemo<SessionContextValue>(
        () => ({
            ...state,
            authorize,
            login,
            logout,
            isAuthenticated: state.status === 'authenticated',
            userName: state.user?.userName,
            permissions: state.user?.roles ?? [],
            userType: state.user?.userType,
            profilePhoto: state.user?.userMediaObj,
            superUserName: state.user?.superUserId ? state.user.superUserName : undefined,
            superUserProfilePhoto: state.user?.superUserId
                ? state.user.superUserMediaObj
                : undefined,
            canRevertToSuperUser: canRevertToSuperUser(state.user),
            isAdmin: state.user?.isAdmin ?? false,
            brandId: parseBrandId(state.user?.brandId),
            refusal
        }),
        [state, refusal, authorize, login, logout]
    );

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
    const ctx = useContext(SessionContext);
    if (!ctx) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return ctx;
}

export function useSessionOptional(): SessionContextValue | null {
    return useContext(SessionContext);
}
